/**
 * The Charter's music cues (M5 spec §20, audio-direction §4.2 and §4.5; plan C19), composed in code with the score's
 * own voices (Zimmer's techniques, never his material; D-035). Data: music.json `charter` (additive; the score's
 * schema ignores it), validated here with Zod. Everything is native Web Audio built per note, like the score's
 * voices (music/voices.ts), into the engine's music bus, so no samples are downloaded.
 *
 * - **The Descendants motif:** a processed throat drone (a glottal saw and its fifth through three vowel formants
 *   that glide every `vowelBars`), hand percussion (pitched skin hits with a noise slap on a 16-step pattern) and a
 *   throat phrase every `phraseEveryBars`. Holdfast systems, and guardian encounters from story tier 3.
 * - **The Isolated Era theme:** the score re-keyed to the `isolated` region (slow aeolian), a pipe-organ figure every
 *   `everyBars` over a low organ pedal. While the `Isolation` state is active.
 * - **Stingers:** short figures per strategic event (`CHARTER_CUES`), the BRAAAM-scale ones also queue the score's
 *   BRAAAM. The bridge (bridge.ts) rate-limits them.
 *
 * Builders take any `BaseAudioContext` (the live one, an offline preview, or the test fake) and a start time, so a
 * cue's graph is deterministic for its inputs (the render hash in the tests). They allocate Web Audio nodes only.
 */
import { z } from 'zod'
import musicJson from '../music/music.json'
import { musicConfig, type MusicConfig } from '../music/musicSchema'
import { degreeMidi, keyFor, midiHz, type Key } from '../music/theory'
import { fmPluck, organNote, organWave, sawStack } from '../music/voices'
import type { OfflineFactory } from '../sfx/render'

/** The Charter stinger cues, in index order (the bridge's `CUE` codes). */
export const CHARTER_CUES = ['claimFlip', 'warDeclared', 'peace', 'gateFlicker', 'gateTremor', 'gateClosure', 'gateReopen', 'voteResult'] as const
export type CharterCue = (typeof CHARTER_CUES)[number]
export const CUE: Readonly<Record<CharterCue, number>> = Object.freeze(Object.fromEntries(CHARTER_CUES.map((c, i) => [c, i])) as Record<CharterCue, number>)

/** The two long-running themes (the bridge's `THEME` codes); `none` = the score alone. */
export const THEME = Object.freeze({ none: 0, descendants: 1, isolated: 2 })
export type ThemeCode = (typeof THEME)[keyof typeof THEME]
export const THEME_NAMES = ['none', 'descendants', 'isolated'] as const

export const VOICES = ['brass', 'organ', 'pluck', 'shimmer', 'throat'] as const
export type CharterVoice = (typeof VOICES)[number]

const db = z.number().min(-60).max(12)
const pos = z.number().positive()
/** [scale degree, octave offset, start 16th, length in 16ths] (the score's motif note format). */
const note = z.tuple([z.number().int().min(-14).max(14), z.number().int().min(-3).max(3), z.number().int().min(0).max(63), z.number().int().min(1).max(64)])
const hits16 = z.array(z.number().min(0).max(1)).length(16)
const formants = z.tuple([pos, pos, pos])

const stingerSchema = z.object({
  voice: z.enum(VOICES),
  notes: z.array(note).min(1).max(16),
  octave: z.number().int().min(0).max(7),
  db,
  velocity: z.number().min(0).max(1),
  /** A higher priority replaces a pending lower one inside the minimum gap. */
  priority: z.number().int().min(0).max(9),
  cooldownSeconds: z.number().nonnegative(),
  /** BRAAAM-scale: also queue the score's BRAAAM (on its next bar, with its own cooldown). */
  braaam: z.boolean(),
})

export const charterMusicSchema = z.object({
  /** Cues start this far ahead of the audio clock (the score's bar callbacks run ahead of the audio too). */
  lookaheadSeconds: z.number().min(0).max(1),
  /** How often the guardian-encounter check scans the ships (from story tier 3). */
  guardianCheckHz: z.number().positive().max(60),
  descendants: z.object({
    db,
    drone: z.object({
      db,
      octave: z.number().int().min(0).max(5),
      fifthDb: db,
      q: pos,
      attack: pos,
      release: pos,
      vowels: z.array(formants).min(1).max(8),
      vowelBars: z.number().int().min(1).max(16),
      glideSeconds: pos,
      wobbleHz: pos,
      wobbleCents: z.number().min(0).max(100),
    }),
    percussion: z.object({
      db,
      patterns: z.array(hits16).min(1),
      lowHz: pos,
      highHz: pos,
      /** Hits at or above this velocity use the high skin. */
      accent: z.number().min(0).max(1),
      decay: pos,
      noiseDb: db,
      noiseHz: pos,
    }),
    phrases: z.array(z.array(note).min(1)).min(1),
    phraseEveryBars: z.number().int().min(1).max(32),
    phraseOctave: z.number().int().min(0).max(6),
    phraseDb: db,
  }),
  isolated: z.object({
    /** The music.json region the score is re-keyed to while the theme plays. */
    region: z.string(),
    db,
    /** Organ figures (scale degrees, one note per `noteBeats`), cycled one per `everyBars`. */
    figures: z.array(z.array(z.number().int().min(-14).max(14)).min(1).max(8)).min(1),
    everyBars: z.number().int().min(1).max(16),
    octave: z.number().int().min(0).max(6),
    noteBeats: pos,
    velocity: z.number().min(0).max(1),
    pedalDb: db,
    pedalOctave: z.number().int().min(0).max(4),
  }),
  stingers: z.object({
    /** No two Charter stingers closer than this (the conductor's hysteresis: they never stack). */
    minGapSeconds: z.number().nonnegative(),
    cues: z.object(Object.fromEntries(CHARTER_CUES.map((c) => [c, stingerSchema])) as Record<CharterCue, typeof stingerSchema>),
  }),
})

export type CharterMusicConfig = z.infer<typeof charterMusicSchema>
export type StingerDef = z.infer<typeof stingerSchema>

/** Parses music.json's `charter` block; the isolated region must exist in the score's regions. */
export function parseCharterMusic(raw: unknown, score: MusicConfig = musicConfig): CharterMusicConfig {
  const cfg = charterMusicSchema.parse(raw)
  if (score.regions[cfg.isolated.region] === undefined) throw new Error(`music.json charter: unknown isolated region ${cfg.isolated.region}`)
  const p = cfg.descendants.percussion
  if (p.highHz <= p.lowHz) throw new Error('music.json charter: percussion.highHz must be above lowHz')
  return cfg
}

export const charterMusic: CharterMusicConfig = parseCharterMusic((musicJson as { charter?: unknown }).charter)

const dbGain = (d: number): number => Math.pow(10, d / 20)

/** Seconds per sixteenth at the key's tempo. */
export function sixteenth(key: Key): number {
  return 15 / key.bpm
}

/** Seconds per bar (4/4) at the key's tempo. */
export function barSeconds(key: Key): number {
  return 240 / key.bpm
}

/** Per-context resources shared by the cues (the organ wave, the noise burst). Cached per context. */
interface Kit {
  organ: PeriodicWave
  noise: AudioBuffer
}
const kits = new WeakMap<BaseAudioContext, Kit>()

function kitOf(ac: BaseAudioContext, score: MusicConfig): Kit {
  let k = kits.get(ac)
  if (k === undefined) {
    const n = Math.max(1, Math.round(ac.sampleRate * 0.08))
    const noise = ac.createBuffer(1, n, ac.sampleRate)
    const d = noise.getChannelData(0)
    // A fixed LCG: the slap is identical in every context (deterministic renders).
    let s = 0x2545f491
    for (let i = 0; i < n; i++) {
      s = (Math.imul(s, 1664525) + 1013904223) >>> 0
      d[i] = (s / 0x80000000 - 1) * (1 - i / n)
    }
    k = { organ: organWave(ac, score.instruments.organ.partials), noise }
    kits.set(ac, k)
  }
  return k
}

/** A cue's output gain into `out`. */
function cueBus(ac: BaseAudioContext, out: AudioNode, gainDb: number): GainNode {
  const g = ac.createGain()
  g.gain.value = dbGain(gainDb)
  g.connect(out)
  return g
}

/** A throat note: a glottal saw through three vowel formants (the Descendants' processed voice). */
function throatNote(ac: BaseAudioContext, dest: AudioNode, t: number, dur: number, hz: number, vel: number, vowel: readonly number[], q: number): void {
  const env = ac.createGain()
  env.gain.setValueAtTime(0, t)
  env.gain.linearRampToValueAtTime(vel, t + Math.min(0.25, dur * 0.4))
  env.gain.setValueAtTime(vel, t + dur)
  env.gain.setTargetAtTime(0, t + dur, 0.2)
  env.connect(dest)
  const o = ac.createOscillator()
  o.type = 'sawtooth'
  o.frequency.value = hz
  for (let i = 0; i < 3; i++) {
    const f = ac.createBiquadFilter()
    f.type = 'bandpass'
    f.frequency.value = vowel[i]
    f.Q.value = q
    const g = ac.createGain()
    g.gain.value = 1 / (i + 1)
    o.connect(f)
    f.connect(g)
    g.connect(env)
  }
  o.start(t)
  o.stop(t + dur + 1.2)
}

/** A high, subtle shimmer: a sine and a slightly sharp octave that swell and fade (the gate's first flicker). */
function shimmerNote(ac: BaseAudioContext, dest: AudioNode, t: number, dur: number, hz: number, vel: number): void {
  const env = ac.createGain()
  env.gain.setValueAtTime(0, t)
  env.gain.linearRampToValueAtTime(vel, t + dur * 0.5)
  env.gain.setTargetAtTime(0, t + dur * 0.5, dur / 4)
  env.connect(dest)
  const end = t + dur * 1.6
  for (let i = 0; i < 2; i++) {
    const o = ac.createOscillator()
    o.type = 'sine'
    o.frequency.value = hz * (i === 0 ? 1 : 2.006)
    o.connect(env)
    o.start(t)
    o.stop(end)
  }
}

/** One note of a Charter voice. */
export function voiceNote(ac: BaseAudioContext, dest: AudioNode, voice: CharterVoice, t: number, dur: number, hz: number, vel: number, score: MusicConfig = musicConfig, cfg: CharterMusicConfig = charterMusic): void {
  const inst = score.instruments
  switch (voice) {
    case 'brass': {
      const s = inst.sting
      const f = ac.createBiquadFilter()
      f.type = 'lowpass'
      f.frequency.value = s.cutoff
      f.connect(dest)
      sawStack(ac, f, t, dur, hz, vel, s.saws, s.detuneCents, s.attack, Math.max(s.decay, dur * 0.25))
      break
    }
    case 'organ':
      organNote(ac, dest, t, dur, hz, vel, kitOf(ac, score).organ, inst.organ)
      break
    case 'pluck':
      fmPluck(ac, dest, t, hz, vel, inst.pluck)
      break
    case 'shimmer':
      shimmerNote(ac, dest, t, dur, hz, vel)
      break
    case 'throat': {
      const d = cfg.descendants.drone
      throatNote(ac, dest, t, dur, hz, vel, d.vowels[0], d.q)
      break
    }
  }
}

/** Schedules motif notes ([degree, octave offset, 16th, length]) from `t0` in `key`. */
function playNotes(ac: BaseAudioContext, dest: AudioNode, voice: CharterVoice, notes: readonly (readonly number[])[], key: Key, octave: number, t0: number, vel: number, score: MusicConfig, cfg: CharterMusicConfig): void {
  const s = sixteenth(key)
  for (const n of notes) voiceNote(ac, dest, voice, t0 + n[2] * s, n[3] * s, midiHz(degreeMidi(key, n[0], octave + n[1])), vel, score, cfg)
}

/** Schedules a stinger from `t0`. Returns its length in seconds (the last note's end). */
export function scheduleStinger(ac: BaseAudioContext, out: AudioNode, cue: CharterCue, key: Key, t0: number, cfg: CharterMusicConfig = charterMusic, score: MusicConfig = musicConfig): number {
  const def = cfg.stingers.cues[cue]
  const bus = cueBus(ac, out, def.db)
  playNotes(ac, bus, def.voice, def.notes, key, def.octave, t0, def.velocity, score, cfg)
  let end = 0
  for (const n of def.notes) end = Math.max(end, n[2] + n[3])
  return end * sixteenth(key)
}

/** A hand-percussion hit: a pitched skin with a falling pitch, plus a short noise slap. */
function skinHit(ac: BaseAudioContext, dest: AudioNode, t: number, vel: number, hz: number, p: CharterMusicConfig['descendants']['percussion'], noise: AudioBuffer): void {
  const o = ac.createOscillator()
  o.type = 'sine'
  o.frequency.setValueAtTime(hz * 1.6, t)
  o.frequency.exponentialRampToValueAtTime(hz, t + 0.03)
  const env = ac.createGain()
  env.gain.setValueAtTime(vel, t)
  env.gain.setTargetAtTime(0, t, p.decay / 4)
  o.connect(env)
  env.connect(dest)
  o.start(t)
  o.stop(t + p.decay * 1.5)
  const src = ac.createBufferSource()
  src.buffer = noise
  const f = ac.createBiquadFilter()
  f.type = 'bandpass'
  f.frequency.value = p.noiseHz
  const g = ac.createGain()
  g.gain.value = vel * dbGain(p.noiseDb)
  src.connect(f)
  f.connect(g)
  g.connect(dest)
  src.start(t)
}

/** A running Descendants drone (built when the theme starts, released when it ends). */
export interface DroneHandle {
  /** Glides the formants to vowel `index` at `t`. */
  vowel(index: number, t: number): void
  /** Fades out from `t` and stops the oscillators after the release. */
  release(t: number): void
}

/** Starts the throat drone on the key's tonic and fifth at `t0` into `out`. */
export function startDrone(ac: BaseAudioContext, out: AudioNode, key: Key, t0: number, cfg: CharterMusicConfig = charterMusic): DroneHandle {
  const d = cfg.descendants.drone
  const bus = ac.createGain()
  bus.gain.setValueAtTime(0, t0)
  bus.gain.linearRampToValueAtTime(dbGain(cfg.descendants.db + d.db), t0 + d.attack)
  bus.connect(out)
  const filters: BiquadFilterNode[] = []
  const mix = ac.createGain()
  mix.gain.value = 1
  for (let i = 0; i < 3; i++) {
    const f = ac.createBiquadFilter()
    f.type = 'bandpass'
    f.frequency.value = d.vowels[0][i]
    f.Q.value = d.q
    const g = ac.createGain()
    g.gain.value = 1 / (i + 1)
    mix.connect(f)
    f.connect(g)
    g.connect(bus)
    filters.push(f)
  }
  const root = midiHz(degreeMidi(key, 0, d.octave))
  const oscs: OscillatorNode[] = []
  const lfo = ac.createOscillator()
  lfo.type = 'sine'
  lfo.frequency.value = d.wobbleHz
  const depth = ac.createGain()
  depth.gain.value = d.wobbleCents
  lfo.connect(depth)
  for (let i = 0; i < 2; i++) {
    const o = ac.createOscillator()
    o.type = 'sawtooth'
    o.frequency.value = i === 0 ? root : root * 1.5
    depth.connect(o.detune)
    const g = ac.createGain()
    g.gain.value = i === 0 ? 1 : dbGain(d.fifthDb)
    o.connect(g)
    g.connect(mix)
    o.start(t0)
    oscs.push(o)
  }
  lfo.start(t0)
  oscs.push(lfo)
  let released = false
  return {
    vowel(index: number, t: number): void {
      if (released) return
      const v = d.vowels[((index % d.vowels.length) + d.vowels.length) % d.vowels.length]
      for (let i = 0; i < 3; i++) filters[i].frequency.setTargetAtTime(v[i], t, d.glideSeconds / 3)
    },
    release(t: number): void {
      if (released) return
      released = true
      bus.gain.cancelScheduledValues(t)
      bus.gain.setValueAtTime(bus.gain.value, t)
      bus.gain.setTargetAtTime(0, t, d.release / 4)
      for (const o of oscs) o.stop(t + d.release * 1.5)
    },
  }
}

/**
 * One bar of the Descendants motif from `t0` (bar number `bar` picks the pattern, the phrase and the vowel): the
 * hand percussion, and a throat phrase every `phraseEveryBars`. The drone (if any) glides its vowel.
 */
export function scheduleDescendantsBar(ac: BaseAudioContext, out: AudioNode, bar: number, key: Key, t0: number, drone: DroneHandle | null, cfg: CharterMusicConfig = charterMusic, score: MusicConfig = musicConfig): void {
  const c = cfg.descendants
  const p = c.percussion
  const s = sixteenth(key)
  const kit = kitOf(ac, score)
  const perc = cueBus(ac, out, c.db + p.db)
  const pattern = p.patterns[bar % p.patterns.length]
  for (let i = 0; i < 16; i++) {
    const v = pattern[i]
    if (v <= 0) continue
    skinHit(ac, perc, t0 + i * s, v, v >= p.accent ? p.highHz : p.lowHz, p, kit.noise)
  }
  if (drone !== null && bar % c.drone.vowelBars === 0) drone.vowel(Math.floor(bar / c.drone.vowelBars), t0)
  if (bar % c.phraseEveryBars === 0) {
    const phrase = c.phrases[Math.floor(bar / c.phraseEveryBars) % c.phrases.length]
    const bus = cueBus(ac, out, c.db + c.phraseDb)
    const vowels = c.drone.vowels
    for (let i = 0; i < phrase.length; i++) {
      const n = phrase[i]
      throatNote(ac, bus, t0 + n[2] * s, n[3] * s, midiHz(degreeMidi(key, n[0], c.phraseOctave + n[1])), 0.8, vowels[i % vowels.length], c.drone.q)
    }
  }
}

/** One bar of the Isolated Era theme from `t0`: a pipe-organ figure every `everyBars`, over the low pedal. */
export function scheduleIsolatedBar(ac: BaseAudioContext, out: AudioNode, bar: number, key: Key, t0: number, cfg: CharterMusicConfig = charterMusic, score: MusicConfig = musicConfig): void {
  const c = cfg.isolated
  if (bar % c.everyBars !== 0) return
  const beat = 4 * sixteenth(key)
  const bus = cueBus(ac, out, c.db)
  const organ = kitOf(ac, score).organ
  const span = c.everyBars * barSeconds(key)
  organNote(ac, bus, t0, span, midiHz(degreeMidi(key, 0, c.pedalOctave)), c.velocity * dbGain(c.pedalDb), organ, score.instruments.organ)
  const figure = c.figures[Math.floor(bar / c.everyBars) % c.figures.length]
  const dur = c.noteBeats * beat
  for (let i = 0; i < figure.length; i++) organNote(ac, bus, t0 + i * dur, dur, midiHz(degreeMidi(key, figure[i], c.octave)), c.velocity, organ, score.instruments.organ)
}

// ---------------------------------------------------------------------------------------------------------------
// Offline previews (the audio lab and tools/audio/render-previews.ts; the user's listening checklist)
// ---------------------------------------------------------------------------------------------------------------

export interface CharterPreview {
  readonly id: string
  readonly title: string
  readonly seconds: number
}

export const CHARTER_PREVIEWS: readonly CharterPreview[] = [
  { id: '50-charter-descendants', title: 'The Descendants motif: throat drone, hand percussion and a throat phrase (8 bars, deepReach key)', seconds: 20 },
  { id: '51-charter-isolated', title: 'The Isolated Era theme: slow organ figures over the pedal (8 bars, the isolated key)', seconds: 30 },
  ...CHARTER_CUES.map((c): CharterPreview => ({ id: `52-charter-${c}`, title: `Charter stinger: ${c}`, seconds: 6 })),
]

/** The key a preview plays in. */
function previewKey(id: string, score: MusicConfig, cfg: CharterMusicConfig): Key {
  if (id === '51-charter-isolated') return keyFor(score, cfg.isolated.region, score.seed)
  return keyFor(score, score.regions.deepReach !== undefined ? 'deepReach' : score.region, score.seed)
}

/** Builds a preview's graph into `out` of `ac` from t = 0 (the render hash tests use it on the fake). */
export function buildCharterPreview(ac: BaseAudioContext, out: AudioNode, id: string, cfg: CharterMusicConfig = charterMusic, score: MusicConfig = musicConfig): void {
  const key = previewKey(id, score, cfg)
  const bar = barSeconds(key)
  if (id === '50-charter-descendants') {
    const drone = startDrone(ac, out, key, 0, cfg)
    for (let b = 0; b < 8; b++) scheduleDescendantsBar(ac, out, b, key, b * bar, drone, cfg, score)
    drone.release(8 * bar)
    return
  }
  if (id === '51-charter-isolated') {
    for (let b = 0; b < 8; b++) scheduleIsolatedBar(ac, out, b, key, b * bar, cfg, score)
    return
  }
  const cue = CHARTER_CUES.find((c) => id === `52-charter-${c}`)
  if (cue === undefined) throw new Error(`unknown charter preview ${id}`)
  scheduleStinger(ac, out, cue, key, 0.1, cfg, score)
}

/** Renders a preview offline (the audio lab's `renderMusic` route for these ids). */
export async function renderCharterPreview(factory: OfflineFactory, id: string, sampleRate: number): Promise<AudioBuffer> {
  const p = CHARTER_PREVIEWS.find((x) => x.id === id)
  if (p === undefined) throw new Error(`unknown charter preview ${id}`)
  const ctx = factory(2, Math.ceil(p.seconds * sampleRate), sampleRate)
  buildCharterPreview(ctx, ctx.destination, id)
  return ctx.startRendering()
}
